import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { SolutionSummaryFacts, convertSBCPrice } from "@repo/shared/utils";
import { Clock } from "lucide-react";
import { ReactNode } from "react";

type Size = "default" | "compact" | "dense" | "small";

const ICON: Record<Size, number> = {
  default: 12,
  compact: 10,
  dense: 10,
  small: 10,
};
const VALUE: Record<Size, string> = {
  default: "text-2xl",
  compact: "text-xl",
  dense: "text-lg",
  small: "text-base",
};
/* Every column the same width, so a wide Market value does not push Club off centre. */
const MIN_W: Record<Size, string> = {
  default: "min-w-[76px]",
  compact: "min-w-16",
  dense: "min-w-[72px]",
  small: "min-w-16",
};
const LABEL: Record<Size, string> = {
  default: "text-xs",
  compact: "text-xs",
  dense: "text-xxs",
  small: "text-xxs",
};

/* While a new solve is in flight the old numbers must not read as the new
   ones, so every value shows a skeleton instead. */
const PENDING = <Skeleton className="w-14" />;
const PENDING_SUB = <Skeleton className="w-16" />;

const Figure = ({
  label,
  price,
  size,
  loading = false,
  muted = false,
  sub,
}: {
  label: string;
  price: number;
  size: Size;
  loading?: boolean;
  /** Greyed: context for the other figures, not the headline. */
  muted?: boolean;
  /** The count behind the coins, e.g. "8 players" under the club value. */
  sub?: ReactNode;
}) => (
  <div
    className={cn(
      "flex flex-col",
      MIN_W[size],
      size === "dense" ? "gap-0.5" : "gap-1",
      (size === "compact" || size === "dense") && "items-center text-center",
    )}
  >
    {/* The coin sits after the title, so the value below is a bare number. */}
    <span
      className={cn(
        "flex items-center gap-1.5 font-bold uppercase tracking-wide text-foreground-faint",
        LABEL[size],
      )}
    >
      {label}
      {/* Dimmed and washed out: a hint of the unit, not a second headline. */}
      <span className="flex opacity-70 grayscale-[20%]">
        <CoinIcon size={ICON[size]} />
      </span>
    </span>
    <span
      className={cn(
        "font-extrabold -tracking-[0.02em]",
        VALUE[size],
        loading || muted ? "text-foreground-faint" : "text-foreground",
      )}
    >
      {loading ? PENDING : convertSBCPrice(price)}
    </span>
    {sub !== undefined && (
      <span className="text-xs text-foreground-faint">
        {loading ? PENDING_SUB : sub}
      </span>
    )}
  </div>
);

/* The club count as a figure. */
const ClubFigure = ({
  facts,
  size,
  label,
  loading = false,
  total = true,
}: {
  facts: SolutionSummaryFacts;
  size: Size;
  label?: string;
  loading?: boolean;
  /** Show the count as a ratio of the squad, or just the count. */
  total?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <div
      className={cn(
        "flex flex-col",
        MIN_W[size],
        size === "dense" ? "gap-0.5" : "gap-1",
        (size === "compact" || size === "dense") && "items-center text-center",
      )}
    >
      <span
        className={cn(
          "font-bold uppercase tracking-wide text-foreground-faint",
          LABEL[size],
        )}
      >
        {label ?? t("figures.fromClub")}
      </span>
      <span
        className={cn(
          "flex items-center gap-1 font-extrabold -tracking-[0.02em]",
          VALUE[size],
          loading ? "text-foreground-faint" : "text-foreground",
        )}
      >
        {loading ? (
          PENDING
        ) : (
          <span>
            {total
              ? `${facts.ownedCount}/${facts.playerCount}`
              : facts.ownedCount}
          </span>
        )}
      </span>
    </div>
  );
};

const TimeFigure = ({
  facts,
  size,
  loading = false,
}: {
  facts: SolutionSummaryFacts;
  size: Size;
  loading?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <div
      className={cn(
        "flex flex-col",
        MIN_W[size],
        size === "dense" ? "gap-0.5" : "gap-1",
        (size === "compact" || size === "dense") && "items-center text-center",
      )}
    >
      <span
        className={cn(
          "font-bold uppercase tracking-wide text-foreground-faint",
          LABEL[size],
        )}
      >
        {t("figures.time")}
      </span>
      <span
        className={cn(
          "flex items-center gap-2 font-extrabold -tracking-[0.02em] text-foreground-faint",
          VALUE[size],
        )}
      >
        <Clock size={ICON[size]} />
        {loading
          ? PENDING
          : t("figures.estimate", { minutes: facts.estimatedMinutes })}
      </span>
    </div>
  );
};

const count = { b: <span className="font-bold text-foreground-faint" /> };

/* The three counts that sit under the coin figures. */
const subs = (facts: SolutionSummaryFacts) => ({
  club: (
    <Trans
      ns="sbcs"
      i18nKey="figures.playerCount"
      count={facts.ownedCount}
      components={count}
    />
  ),
  market: (
    <Trans
      ns="sbcs"
      i18nKey="figures.transferCount"
      count={facts.transferCount}
      components={count}
    />
  ),
  total: (
    <Trans
      ns="sbcs"
      i18nKey="figures.playerCount"
      count={facts.playerCount}
      components={count}
    />
  ),
});

/**
 * What a squad costs, what it is worth, and how much of it the club covers.
 * Shared by every SBC solution screen: the Streamlined SBC counts "Transfer cost" down as
 * cards are marked, the solver shows the whole market cost, and neither knows
 * the difference from here.
 *
 * `compact` stacks the club block under the figures; `dense` puts everything
 * on one line at phone size and drops the counts.
 */
export const SummaryFigures = ({
  facts,
  compact = false,
  dense = false,
  loading = false,
  showEstimate = true,
}: {
  facts: SolutionSummaryFacts;
  compact?: boolean;
  dense?: boolean;
  /** A solve is in flight: show dashes rather than the previous squad's numbers. */
  loading?: boolean;
  /* How long a build takes is a Streamline idea: a solution is completed in one
     sitting. The solver submits one squad per challenge, so it says nothing. */
  showEstimate?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  if (dense) {
    // As compact: equal columns filling the row, each centred.
    return (
      <div className="grid grid-flow-col auto-cols-fr items-center gap-2">
        <Figure
          loading={loading}
          label={t("figures.transferCost")}
          price={facts.transferCost}
          size="dense"
        />
        <Figure
          loading={loading}
          label={t("figures.clubValue")}
          price={facts.clubValue}
          size="dense"
        />
        <Figure
          loading={loading}
          label={t("figures.totalValue")}
          price={facts.totalValue}
          size="dense"
        />
        <ClubFigure loading={loading} facts={facts} size="dense" />
      </div>
    );
  }

  const sub = subs(facts);

  if (compact) {
    // Equal columns that fill the row, so nothing is left over on the right.
    return (
      <div className="grid grid-flow-col auto-cols-fr items-start gap-2">
        <Figure
          loading={loading}
          label={t("figures.transferCost")}
          price={facts.transferCost}
          size="compact"
          sub={sub.market}
        />
        <Figure
          loading={loading}
          label={t("figures.clubValue")}
          price={facts.clubValue}
          size="compact"
          sub={sub.club}
        />
        <Figure
          loading={loading}
          label={t("figures.totalValue")}
          price={facts.totalValue}
          size="compact"
          sub={sub.total}
        />
        {showEstimate && (
          <TimeFigure loading={loading} facts={facts} size="compact" />
        )}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <Figure
        loading={loading}
        label={t("figures.transferCost")}
        price={facts.transferCost}
        size="default"
        sub={sub.market}
      />
      <div className="h-[38px] w-px bg-edge" />
      <Figure
        loading={loading}
        label={t("figures.clubValue")}
        price={facts.clubValue}
        size="default"
        sub={sub.club}
      />
      <div className="h-[38px] w-px bg-edge" />
      <Figure
        loading={loading}
        label={t("figures.totalValue")}
        price={facts.totalValue}
        size="default"
        sub={sub.total}
      />
      {showEstimate && (
        <>
          <div className="h-[38px] w-px bg-edge" />
          <TimeFigure loading={loading} facts={facts} size="default" />
        </>
      )}
    </div>
  );
};
